import type { LevelKey, LevelResult } from '../types/quiz'

export const results: Record<LevelKey, LevelResult> = {
  A: {
    key: 'A',
    name: 'Culpa y Vergüenza',
    tag: 'Culpa',
    stage: 'Nivel bajo · autocastigo',
    headline: 'Sigues pagando una cuenta que ya no te corresponde',
    description:
      'Tu mente volvió una y otra vez sobre la misma escena buscando el momento exacto en que "lo arruinaste". Esa búsqueda se siente como responsabilidad, pero es autocastigo: mientras la culpa ocupe el centro, no hay energía disponible para reconstruirte. En PNL trabajamos esto cambiando la manera en que tu memoria está codificada, no repitiéndote que no fue tu culpa.',
    signals: [
      'Repasas la historia buscando tu error, no la lección',
      'Pides perdón incluso cuando no hiciste daño',
      'Sientes que si hubieras sido "suficiente", seguirían juntos',
    ],
    firstStep:
      'Separar lo que hiciste de lo que eres. Son dos cosas distintas y tu cerebro las tiene fusionadas.',
  },
  B: {
    key: 'B',
    name: 'Apatía y Dolor',
    tag: 'Apatía',
    stage: 'Nivel bajo · desesperanza',
    headline: 'No es que estés bien: es que te desconectaste para no sentir',
    description:
      'El vacío no es paz. Es un mecanismo de protección que apagó todo para que el dolor no te desbordara, y de paso apagó también las ganas, el deseo y la curiosidad. Es el nivel que más se confunde con "ya lo superé". Salir de aquí no se trata de motivarte, sino de devolverle movimiento a algo que quedó congelado.',
    signals: [
      'Sientes cansancio más que tristeza',
      'Nada te ilusiona lo suficiente como para moverte',
      'Dormir se volvió tu forma preferida de pasar el día',
    ],
    firstStep:
      'Recuperar un solo punto de energía al día. No motivación: energía. El orden importa.',
  },
  C: {
    key: 'C',
    name: 'Ira y Orgullo',
    tag: 'Ira',
    stage: 'Nivel medio · resentimiento',
    headline: 'Tienes razón, y esa razón te está costando tu libertad',
    description:
      'La rabia te dio algo que la tristeza no te daba: fuerza. Por eso cuesta soltarla. El problema es que esa fuerza sigue apuntando hacia la otra persona, así que tu bienestar aún depende de lo que haga, publique o deje de hacer. Tener la razón y estar libre no siempre son lo mismo, y aquí hay que elegir.',
    signals: [
      'Imaginas la conversación donde por fin entiende lo que te hizo',
      'Quieres estar mejor, pero sobre todo que se note',
      'La injusticia te ocupa más tiempo que tu propio futuro',
    ],
    firstStep:
      'Redirigir la energía de la rabia hacia ti. Es el combustible correcto apuntando al lugar equivocado.',
  },
  D: {
    key: 'D',
    name: 'Coraje y Aceptación',
    tag: 'Coraje',
    stage: 'Nivel medio-alto · en proceso',
    headline: 'Ya estás avanzando; ahora se trata de no retroceder',
    description:
      'Puedes sostener la tristeza sin que te arrastre, y eso es un logro real: significa que tu sistema nervioso ya no está en emergencia. En este nivel el riesgo no es el dolor, es la recaída silenciosa cuando aparece una foto, una fecha o un mensaje. Aquí el trabajo es consolidar, poner estructura y cerrar los cabos que todavía te enganchan.',
    signals: [
      'Duermes mejor, aunque hay días difíciles',
      'Puedes hablar del tema sin desarmarte',
      'Reconstruyes tu confianza poco a poco',
    ],
    firstStep:
      'Cerrar los anclajes que todavía te devuelven al pasado, uno por uno, con un método y no con fuerza de voluntad.',
  },
  E: {
    key: 'E',
    name: 'Amor y Paz',
    tag: 'Amor',
    stage: 'Nivel alto · integrando',
    headline: 'Ya soltaste; lo que sigue es elegir bien',
    description:
      'Puedes mirar la relación completa —lo bueno y lo doloroso— sin que te mueva el piso. Eso es integración real, y no le pasa a la mayoría. Desde aquí el trabajo cambia de tema: ya no es sanar, es no repetir el patrón que te llevó a esa relación y sostener este nivel cuando llegue alguien nuevo.',
    signals: [
      'Piensas en tu ex sin necesidad de resolver nada',
      'Tu identidad ya no depende de esa historia',
      'Te ilusiona el futuro sin miedo de fondo',
    ],
    firstStep:
      'Identificar el patrón de elección que te llevó ahí. Es lo único que decide tu próxima relación.',
  },
}

export const levelOrder: LevelKey[] = ['A', 'B', 'C', 'D', 'E']
